/**
 * Condition-aware prompt resolution for the Personal Statement builder.
 *
 * For each veteran-claimed condition we generate:
 *   1. A "Statement Goal" card copy explaining what a strong statement should cover.
 *   2. A bespoke prompt list (questions targeted at the condition's rating criteria).
 *   3. Quick-Answer chips beneath each prompt — clickable plain-language starters.
 *
 * Architecture:
 *   - `categorize(conditionName)` classifies a free-text condition into a key
 *     used by `CONDITION_PACKAGES`.
 *   - `getConditionPackage(conditionName)` returns the full
 *     { goal, prompts, sideHint } for the editor to render.
 *   - Each prompt carries `whyItMatters` (the reviewer's lens) and `chips`
 *     (curated Quick-Answer chips).
 *
 * Why this is its own module: tying prompts/chips to the condition is what
 * turns the builder from a generic questionnaire into a claim-specific QC
 * coach. Keeping the logic pure means it is fully Jest-testable.
 */

// ---------------------------------------------------------------------------
// 1. Categorization
// ---------------------------------------------------------------------------

const CATEGORY_RULES = [
  // Mental health is detected by the existing isMentalHealthCondition helper
  // — but we still need to map other categories here for non-MH conditions.
  { key: "knee",          re: /\b(knee|patella|patellar|patellofemoral|meniscus|acl|mcl|lcl|pcl|chondromalacia)\b/i },
  { key: "back",          re: /\b(back|lumbar|thoracic|cervical|spine|spinal|disc|sciatic|radiculop|stenosis|degenerative\s+disc)\b/i },
  { key: "hip",           re: /\b(hip|trochanteric|femoroacetabular)\b/i },
  { key: "shoulder",      re: /\b(shoulder|rotator\s+cuff|labrum|labral|impingement|acromio)\b/i },
  { key: "ankle",         re: /\b(ankle|achilles|peroneal|plantar)\b/i },
  { key: "neck",          re: /\b(neck|cervical\s+spine|cervicalgia|whiplash)\b/i },
  { key: "wrist-hand",    re: /\b(wrist|hand|carpal|finger|thumb|metacarpal|cubital)\b/i },
  { key: "foot",          re: /\b(foot|toe|metatars|hallux|pes\s+planus|flat\s+feet)\b/i },
  { key: "tinnitus",      re: /\b(tinnitus|ringing\s+in\s+ears)\b/i },
  { key: "hearing-loss",  re: /\b(hearing\s+loss|hypoacusis|sensorineural)\b/i },
  { key: "sleep-apnea",   re: /\b(sleep\s+apnea|osa|csa|cpap|bipap)\b/i },
  { key: "migraine",      re: /\b(migraine|headache|cephalgia)\b/i },
  { key: "ibs",           re: /\b(ibs|irritable\s+bowel|gerd|reflux|esophagitis|colitis)\b/i },
  { key: "skin",          re: /\b(eczema|psoriasis|dermatitis|acne|rash|scar)\b/i },
];

const SIDE_RULES = [
  { side: "right", re: /\bright\b|\b\(r\)\b|\brt\b/i },
  { side: "left",  re: /\bleft\b|\b\(l\)\b|\blt\b/i },
  { side: "both",  re: /\bbilateral\b|\bboth\b/i },
];

export function categorize(conditionName) {
  if (!conditionName) return "default";
  const s = String(conditionName).toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.re.test(s)) return rule.key;
  }
  return "default";
}

export function detectSide(conditionName) {
  if (!conditionName) return "";
  for (const rule of SIDE_RULES) {
    if (rule.re.test(conditionName)) return rule.side;
  }
  return "";
}

// ---------------------------------------------------------------------------
// 2. Condition packages
// ---------------------------------------------------------------------------

/**
 * Prompt object schema:
 *   { id, section, label, placeholder, whyItMatters, chips }
 *
 * `section` keys match PREVIEW_SECTION_ORDER in statementAudit.js so the
 * sectioned live preview groups answers correctly.
 *
 * `whyItMatters` is a one-line nudge that tells the vet why the reviewer
 * cares — credibility + specificity guidance.
 *
 * `chips` are plain-language starter snippets. Clicking a chip inserts
 * the snippet into the textarea (with smart prepend / append logic in the
 * UI layer).
 */

const KNEE_PACKAGE = {
  goalTemplate: ({ side }) =>
    `Explain when your ${side ? side + " " : ""}knee condition started, what happened during service, how symptoms continued, ` +
    `and how limited flexion affects walking, stairs, kneeling, squatting, standing, repeated use, and flare-ups today.`,
  prompts: [
    {
      id: "side-and-onset",
      section: "service-onset",
      label: "Affected side + onset",
      placeholder: "Which knee — right, left, or both. Approximate month/year symptoms started.",
      whyItMatters: "Reviewers need to know which knee they're rating. Vague (\"my knee\") gets less weight than \"my right knee, starting around July 2014.\"",
      chips: [
        { label: "Right knee", insert: "My right knee" },
        { label: "Left knee", insert: "My left knee" },
        { label: "Both knees", insert: "Both knees, with the right being worse" },
        { label: "Onset ~2014", insert: " — symptoms began around 2014" },
        { label: "Onset during service", insert: " — symptoms began while I was still on active duty" },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service cause (event, PT, duty, deployment, wear-and-tear)",
      placeholder: "Specific injury, PT, duty activities, deployment terrain, maintenance work, or gradual onset.",
      whyItMatters: "Service connection is the hinge of the entire claim. Tie the knee to specific duty activities — PT injuries, jumping from vehicles, repeated kneeling, climbing ladders, ruck marching.",
      chips: [
        { label: "PT injury", insert: "During a PT run / morning formation, I felt a pop in my knee." },
        { label: "Running / PT", insert: "We ran 3–5 miles per day during PT, and my knee began aching after morning runs." },
        { label: "Maintenance work", insert: "My MOS required kneeling and crawling on aircraft / vehicle decks for hours at a time." },
        { label: "Ruck marches", insert: "We carried 60–80 lb rucks on long road marches; my knee swelled after each one." },
        { label: "Vehicle dismounts", insert: "Repeated jumping out of HMMWVs / LMTVs in full kit hammered my knee." },
        { label: "Deployment terrain", insert: "Patrols on rough terrain over the course of a 9-month deployment." },
        { label: "Gradual wear and tear", insert: "There was no single injury — my knee gradually worsened over years of duty wear and tear." },
      ],
    },
    {
      id: "symptoms-during",
      section: "symptoms-service",
      label: "Symptoms during service (pain location, swelling, instability)",
      placeholder: "Where it hurt, how often, swelling, popping, giving way, brace use, sick-call visits.",
      whyItMatters: "Documenting symptoms in service strengthens continuity. Mention sick-call visits, profiles, or buddies who saw you favor the knee.",
      chips: [
        { label: "Pain — front of knee", insert: "Sharp pain over the front of the kneecap, especially with stairs and squats." },
        { label: "Pain — inner side", insert: "Aching pain along the inside of the knee." },
        { label: "Swelling", insert: "The knee would swell after PT and long road marches." },
        { label: "Popping", insert: "I heard and felt popping with deep flexion." },
        { label: "Giving way", insert: "The knee would buckle or give way under load." },
        { label: "Sick call", insert: "I went to sick call multiple times for this knee." },
        { label: "Profile / light duty", insert: "I was placed on a temporary profile / light duty for the knee." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity since separation",
      placeholder: "Symptoms after leaving service. Gaps in care + reasons. Year-by-year if you can.",
      whyItMatters: "Continuity proves the in-service injury didn't resolve. If you had a gap (no insurance, working through it, stigma), explain the gap — it's stronger than pretending it wasn't there.",
      chips: [
        { label: "Symptoms continued", insert: "The knee never returned to baseline after separation; pain and stiffness persisted." },
        { label: "Treated by VA", insert: "I have followed up at the VA since separation for this knee." },
        { label: "Gap — no insurance", insert: "From [year] to [year] I had no insurance and self-managed with OTC pain meds." },
        { label: "Gap — worked through it", insert: "I worked through the pain in civilian jobs because I couldn't afford to stop." },
        { label: "Worsened over years", insert: "Function has worsened year over year, especially after sustained standing." },
      ],
    },
    {
      id: "current-impact",
      section: "current-impact",
      label: "Current impact — flexion, walking, stairs, kneeling, squatting, standing",
      placeholder: "How limited flexion affects daily function. Quantify with distances, durations, frequencies.",
      whyItMatters: "Limitation-of-flexion ratings (38 CFR §4.71a, DC 5260) hinge on degrees and FUNCTIONAL impact. Translate the limitation into real-world activities you can't do or do with pain.",
      chips: [
        { label: "Stairs painful", insert: "Stairs are painful — I take them one at a time, leading with the good leg." },
        { label: "Kneeling painful", insert: "Kneeling is sharply painful; I avoid it whenever possible." },
        { label: "Squatting limited", insert: "I can no longer squat to floor level — my knee won't bend that far without pain." },
        { label: "Walking limited", insert: "I can walk roughly [X] minutes before pain forces me to stop." },
        { label: "Standing limited", insert: "Standing more than [X] minutes causes the knee to ache and stiffen." },
        { label: "Running stopped", insert: "I had to stop running entirely; even short jogs are painful." },
        { label: "Hills / inclines hard", insert: "Inclines and hills are particularly hard on the knee." },
      ],
    },
    {
      id: "flares-repeated-use",
      section: "current-impact",
      label: "Flare-ups + repeated use over time",
      placeholder: "Frequency, duration, what triggers a flare, and what daily function looks like on a worst day.",
      whyItMatters: "VA examiners must consider flare-ups (Mitchell v. Shinseki) but rarely observe them in clinic. Your statement is often the only flare-up evidence in the file.",
      chips: [
        { label: "Flares after walking", insert: "Walking more than [X] minutes triggers a flare." },
        { label: "Flares after standing", insert: "Standing for [X] minutes triggers a flare that can last [Y] hours/days." },
        { label: "Flares — bend much less", insert: "During a flare I can bend the knee much less than usual — even shallow squats are impossible." },
        { label: "Flares — limp", insert: "During a flare I have to limp / use a cane / use a brace." },
        { label: "Repeated use → swelling", insert: "After repeated use (e.g. a day of yard work or shopping) the knee swells and stiffens." },
        { label: "Worst-day function", insert: "On a worst day I can barely climb stairs, kneel, or stand up from a low chair." },
      ],
    },
    {
      id: "instability",
      section: "current-impact",
      label: "Instability / giving way",
      placeholder: "Does it buckle? How often? Have you fallen? Do you wear a brace?",
      whyItMatters: "Instability (DC 5257) is rated SEPARATELY from limitation of motion — both can apply. Quantify frequency and brace use.",
      chips: [
        { label: "Gives way", insert: "The knee gives way / buckles unexpectedly — about [X] times per week / month." },
        { label: "Caused fall", insert: "I have fallen because of the knee giving way." },
        { label: "Uses brace", insert: "I wear a knee brace [daily / for activities / on worst days]." },
        { label: "Uses cane", insert: "I use a cane on bad days to take pressure off the knee." },
        { label: "No instability", insert: "I do not have buckling, but I avoid activities that would test it." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — PT, meds, imaging, surgery, profiles",
      placeholder: "Current and past treatment. Imaging results. Medications. Profiles or modified duty.",
      whyItMatters: "Treatment evidence corroborates the symptom severity. Name the clinic, doses, frequencies, and imaging dates so a reviewer can match to your records.",
      chips: [
        { label: "Physical therapy", insert: "I attended physical therapy at the VA [clinic / location] for [X] sessions in [year]." },
        { label: "Medication", insert: "I take [med name + dose + frequency] for knee pain." },
        { label: "OTC pain meds", insert: "I rely on OTC NSAIDs (ibuprofen / naproxen) for flare days." },
        { label: "X-ray / MRI", insert: "Imaging in [month/year] showed [findings]." },
        { label: "Cortisone injection", insert: "I received a cortisone injection in [date] for this knee." },
        { label: "Brace prescribed", insert: "A knee brace was prescribed by [provider] in [date]." },
        { label: "Profile / modified duty", insert: "I was on a profile / modified duty for the knee from [date] to [date]." },
      ],
    },
    {
      id: "work-impact",
      section: "current-impact",
      label: "Work / PT / missed days",
      placeholder: "Missed work, missed PT, modified duties, jobs you can't perform now.",
      whyItMatters: "Occupational impact is part of the rating analysis. Concrete missed days or job changes anchor the claim in real-world cost.",
      chips: [
        { label: "Missed work", insert: "I have missed approximately [X] days of work in the last 12 months due to the knee." },
        { label: "Missed PT", insert: "I missed PT / formation multiple times during service for this knee." },
        { label: "Modified duties", insert: "I am on modified duties at my civilian job because of the knee." },
        { label: "Quit physical job", insert: "I had to leave a physical job because my knee could no longer keep up." },
        { label: "Job change", insert: "I changed careers / went to a desk job because of the knee." },
      ],
    },
  ],
};

const BACK_PACKAGE = {
  goalTemplate: () =>
    "Explain when your back condition started, what happened during service, how symptoms continued, " +
    "and how range-of-motion limits, flare-ups, radiculopathy, and incapacitating episodes affect daily function today.",
  prompts: [
    {
      id: "side-and-onset",
      section: "service-onset",
      label: "Region affected + onset",
      placeholder: "Lumbar / thoracic / cervical. Approximate onset month/year.",
      whyItMatters: "Different regions have different rating criteria. Be specific: \"lower back (lumbar)\" not just \"back.\"",
      chips: [
        { label: "Lumbar (lower back)", insert: "My lower back (lumbar spine)" },
        { label: "Thoracic (mid-back)", insert: "My mid-back (thoracic spine)" },
        { label: "Cervical (neck)", insert: "My neck (cervical spine)" },
        { label: "Onset during service", insert: " — symptoms started while on active duty" },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service cause (injury, lifting, ruck, MOS)",
      placeholder: "Specific event or duty exposure — lifting, vehicle drops, parachute landings, ruck marches.",
      whyItMatters: "Service connection requires linking the back to duty activities. Rucking, lifting, vehicle accidents, and parachute jumps are well-recognized causes.",
      chips: [
        { label: "Lifting injury", insert: "I injured my back lifting [equipment/load] during [duty]." },
        { label: "Ruck marches", insert: "Long road marches with heavy rucks (60–80 lb) progressively hurt my back." },
        { label: "Parachute landing", insert: "Multiple parachute landings caused jarring impacts on my spine." },
        { label: "Vehicle crash", insert: "I was in a vehicle accident / rollover during service that injured my back." },
        { label: "Body armor weight", insert: "Wearing body armor and gear (40+ lb) for long shifts strained my back." },
        { label: "Gradual wear and tear", insert: "There was no single injury — my back gradually worsened from duty wear and tear." },
      ],
    },
    {
      id: "symptoms-during",
      section: "symptoms-service",
      label: "Symptoms during service",
      placeholder: "Pain location, radiation down legs/arms, numbness, tingling, sick-call visits.",
      whyItMatters: "Radiation, numbness, and tingling suggest radiculopathy — a separately ratable condition.",
      chips: [
        { label: "Lower back pain", insert: "Pain in my lower back, worst after physical activity." },
        { label: "Pain radiates to leg", insert: "Pain radiated down my [right/left] leg." },
        { label: "Numbness / tingling", insert: "I had numbness and tingling in my [leg/foot/arm]." },
        { label: "Sick call", insert: "I went to sick call multiple times for back pain." },
        { label: "Stiffness on waking", insert: "My back was stiff every morning after PT." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity since separation",
      placeholder: "Symptoms after leaving service. Gaps in care + reasons. Year-by-year if you can.",
      whyItMatters: "Continuity proves the back injury never resolved. If you had a gap, explain why.",
      chips: [
        { label: "Symptoms continued", insert: "Back pain never resolved after separation." },
        { label: "Treated by VA", insert: "I have been treated at the VA for my back since separation." },
        { label: "Gap — no insurance", insert: "From [year] to [year] I had no insurance and self-managed." },
        { label: "Worsened over years", insert: "My back has worsened year over year." },
      ],
    },
    {
      id: "current-impact",
      section: "current-impact",
      label: "Current impact — bending, sitting, standing, lifting, sleeping",
      placeholder: "How range-of-motion limits affect daily function. Quantify with durations.",
      whyItMatters: "VA back ratings (38 CFR §4.71a) are tied to forward flexion degrees AND functional limitation. Translate the limits into real activities.",
      chips: [
        { label: "Can't bend forward", insert: "I cannot bend forward to touch my toes without sharp pain." },
        { label: "Sitting limited", insert: "Sitting for more than [X] minutes triggers pain and stiffness." },
        { label: "Standing limited", insert: "Standing for [X] minutes makes my back ache." },
        { label: "Lifting limited", insert: "I cannot lift more than [X] lb without back pain." },
        { label: "Sleep disrupted", insert: "Back pain wakes me up multiple times per night." },
        { label: "Driving painful", insert: "Long drives ([X]+ minutes) trigger back pain." },
      ],
    },
    {
      id: "flares-repeated-use",
      section: "current-impact",
      label: "Flare-ups + incapacitating episodes",
      placeholder: "Frequency, duration, what triggers a flare. Days you could not get out of bed.",
      whyItMatters: "Incapacitating episodes (bed rest prescribed by a doctor) are separately ratable for intervertebral disc syndrome (DC 5243).",
      chips: [
        { label: "Bed-bound days", insert: "I have been bed-bound for [X] days in the last 12 months due to back flares." },
        { label: "Flares after lifting", insert: "Lifting / moving / certain twists trigger flares lasting [Y] days." },
        { label: "Worst-day function", insert: "On a worst day I cannot bend, lift, sit for long, or sleep through the night." },
      ],
    },
    {
      id: "radiculopathy",
      section: "current-impact",
      label: "Radiculopathy — leg/arm symptoms",
      placeholder: "Numbness, tingling, weakness, electric pain down a limb.",
      whyItMatters: "Radiculopathy is a separate diagnostic code (DC 8520+ for legs, 8510+ for arms). Document side, severity, and frequency.",
      chips: [
        { label: "Numbness — leg", insert: "I have numbness and tingling down my [right/left] leg." },
        { label: "Numbness — arm", insert: "I have numbness and tingling down my [right/left] arm and into my hand." },
        { label: "Electric pain", insert: "I get electric-shock pain shooting down the limb when I move wrong." },
        { label: "Weakness", insert: "I have weakness in my [limb] that affects [activity]." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — PT, meds, imaging, injections, surgery",
      placeholder: "Current treatment. Imaging results. Meds. Devices (brace, TENS).",
      whyItMatters: "Treatment corroborates severity. Name clinicians, clinics, meds, and imaging dates.",
      chips: [
        { label: "Physical therapy", insert: "I have done physical therapy at [clinic] for [X] sessions." },
        { label: "Medication", insert: "I take [med name + dose + frequency] for back pain." },
        { label: "X-ray / MRI", insert: "Imaging in [month/year] showed [findings — disc, stenosis, DDD]." },
        { label: "Epidural injection", insert: "I received an epidural steroid injection in [date]." },
        { label: "TENS / brace", insert: "I use a [TENS unit / back brace] for pain control." },
        { label: "Surgery", insert: "I had spine surgery ([procedure]) in [date]." },
      ],
    },
    {
      id: "work-impact",
      section: "current-impact",
      label: "Work / missed days / job changes",
      placeholder: "Missed work, modified duties, jobs you can't perform now.",
      whyItMatters: "Occupational impact anchors the claim in real-world cost.",
      chips: [
        { label: "Missed work", insert: "I have missed [X] days of work in the last 12 months due to my back." },
        { label: "Modified duties", insert: "I am on modified duties because of my back." },
        { label: "Job change", insert: "I had to leave a [physical / labor] job because of my back." },
      ],
    },
  ],
};

// Generic musculoskeletal package factory — used for shoulder / hip / ankle / etc.
function makeMskPackage(jointLabel, dcNote) {
  return {
    goalTemplate: ({ side }) =>
      `Explain when your ${side ? side + " " : ""}${jointLabel} condition started, what happened during service, ` +
      `how symptoms continued, and how range-of-motion limits, flare-ups, and functional limits affect daily activities today.`,
    prompts: [
      {
        id: "side-and-onset",
        section: "service-onset",
        label: `Affected ${jointLabel} + onset`,
        placeholder: `Right / left / both. Approximate month/year symptoms started.`,
        whyItMatters: `Reviewers need a specific ${jointLabel} and a date anchor.`,
        chips: [
          { label: `Right ${jointLabel}`, insert: `My right ${jointLabel}` },
          { label: `Left ${jointLabel}`, insert: `My left ${jointLabel}` },
          { label: `Both ${jointLabel}s`, insert: `Both ${jointLabel}s, with one being worse` },
          { label: "Onset during service", insert: " — symptoms started while on active duty" },
        ],
      },
      {
        id: "service-event",
        section: "service-onset",
        label: "In-service cause",
        placeholder: "Specific injury, PT, duty activity, deployment, MOS, gradual wear.",
        whyItMatters: `Tie the ${jointLabel} to specific duty exposures so the reviewer can establish service connection.`,
        chips: [
          { label: "PT injury", insert: `I injured my ${jointLabel} during a PT activity.` },
          { label: "Lifting / overhead", insert: `Lifting / overhead work in my MOS strained my ${jointLabel}.` },
          { label: "Gradual wear and tear", insert: `My ${jointLabel} gradually worsened from duty wear and tear.` },
        ],
      },
      {
        id: "symptoms-during",
        section: "symptoms-service",
        label: "Symptoms during service",
        placeholder: "Pain, instability, swelling, sick-call visits.",
        whyItMatters: "Documented symptoms in service strengthen continuity.",
        chips: [
          { label: "Pain with use", insert: `Pain whenever I used my ${jointLabel} for overhead / loaded / repeated tasks.` },
          { label: "Sick call", insert: `I went to sick call for this ${jointLabel}.` },
          { label: "Profile", insert: `I was placed on a profile / modified duty for this ${jointLabel}.` },
        ],
      },
      {
        id: "continuity",
        section: "continuity",
        label: "Continuity since separation",
        placeholder: "Symptoms after leaving service. Gaps in care + reasons.",
        whyItMatters: "Continuity proves the injury never resolved.",
        chips: [
          { label: "Symptoms continued", insert: `${jointLabel.charAt(0).toUpperCase() + jointLabel.slice(1)} symptoms continued after separation.` },
          { label: "Treated by VA", insert: `I have followed up at VA for this ${jointLabel}.` },
          { label: "Worsened over years", insert: `Symptoms have worsened year over year.` },
        ],
      },
      {
        id: "current-impact",
        section: "current-impact",
        label: "Current functional impact",
        placeholder: "How limits affect work, sleep, hobbies, daily tasks.",
        whyItMatters: dcNote || `Translate the limitation into real-world activities.`,
        chips: [
          { label: "Limited motion", insert: `I cannot move the ${jointLabel} through its full range without pain.` },
          { label: "Pain with activity", insert: `Daily activities trigger pain — [examples].` },
          { label: "Sleep disrupted", insert: `${jointLabel.charAt(0).toUpperCase() + jointLabel.slice(1)} pain wakes me up at night.` },
        ],
      },
      {
        id: "flares-repeated-use",
        section: "current-impact",
        label: "Flare-ups + repeated use",
        placeholder: "Frequency, duration, what triggers a flare. Worst-day function.",
        whyItMatters: "Examiners rarely see you on a flare day — your statement is often the only flare-up evidence.",
        chips: [
          { label: "Flares after repeated use", insert: `Repeated use triggers flares that last [X] hours/days.` },
          { label: "Worst-day function", insert: `On a worst day I cannot [activities].` },
        ],
      },
      {
        id: "treatment",
        section: "treatment",
        label: "Treatment — PT, meds, imaging, devices",
        placeholder: "Current treatment, imaging, meds, braces / aids.",
        whyItMatters: "Treatment corroborates severity. Name clinics, meds, devices.",
        chips: [
          { label: "Physical therapy", insert: "I attended physical therapy at [clinic] for [X] sessions." },
          { label: "Medication", insert: "I take [med name + dose + frequency] for pain." },
          { label: "Imaging", insert: "Imaging in [month/year] showed [findings]." },
          { label: "Brace / device", insert: "I use a [brace / device] for support." },
        ],
      },
      {
        id: "work-impact",
        section: "current-impact",
        label: "Work / missed days",
        placeholder: "Missed work, modified duties, jobs you can no longer perform.",
        whyItMatters: "Occupational impact anchors the claim in real-world cost.",
        chips: [
          { label: "Missed work", insert: `I have missed [X] days of work in the last 12 months due to this ${jointLabel}.` },
          { label: "Modified duties", insert: "I am on modified duties at my civilian job." },
        ],
      },
    ],
  };
}

const TINNITUS_PACKAGE = {
  goalTemplate: () =>
    "Explain when your tinnitus started, the in-service noise exposure that caused it, how it continued after service, " +
    "and how the constant ringing affects sleep, concentration, conversation, and daily quality of life today.",
  prompts: [
    {
      id: "onset",
      section: "service-onset",
      label: "When did the ringing start?",
      placeholder: "Approximate date + the noise event / exposure that started it.",
      whyItMatters: "Tinnitus claims hinge on linking the ringing to a specific service-era noise exposure.",
      chips: [
        { label: "Noticed during service", insert: "I first noticed the ringing while still on active duty." },
        { label: "After specific event", insert: "I noticed the ringing immediately after [IED, mortar, weapons fire, mechanical noise]." },
        { label: "Gradual", insert: "The ringing came on gradually over [X] years of noise exposure." },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service noise exposure",
      placeholder: "Weapons, vehicles, aircraft, generators, mortar / IED, MOS-specific noise.",
      whyItMatters: "MOS noise exposure (gunner, mechanic, pilot, infantry) is widely recognized — be specific about what you were exposed to.",
      chips: [
        { label: "Weapons fire", insert: "I was exposed to weapons fire (M16/M4, M249, M240, .50 cal) regularly." },
        { label: "Artillery / mortars", insert: "I was exposed to artillery / mortar fire during [training / deployment]." },
        { label: "IED / blast", insert: "I was near an IED / blast event during [date / location]." },
        { label: "Aircraft / jet noise", insert: "I worked on / near aircraft engines without consistent hearing protection." },
        { label: "Generators / vehicles", insert: "I worked around constant generator / vehicle noise." },
        { label: "Hearing protection inconsistent", insert: "Hearing protection was inconsistent — often unavailable during incoming fire or rapid response." },
      ],
    },
    {
      id: "symptoms-then",
      section: "symptoms-service",
      label: "Symptoms during service",
      placeholder: "Was the ringing intermittent or constant? Did you report it?",
      whyItMatters: "Documenting in-service onset (sick call, hearing tests, or buddy statements) strengthens the claim.",
      chips: [
        { label: "Intermittent at first", insert: "The ringing was intermittent at first, after loud events." },
        { label: "Became constant", insert: "It became constant during service." },
        { label: "Did not report", insert: "I did not report it because it was common in my unit." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity since separation",
      placeholder: "Has the ringing been continuous? Worsened?",
      whyItMatters: "Continuous symptoms since service support service connection.",
      chips: [
        { label: "Constant since service", insert: "The ringing has been constant since separation." },
        { label: "Worsened", insert: "It has worsened over the years." },
      ],
    },
    {
      id: "current-impact",
      section: "current-impact",
      label: "Current impact — sleep, concentration, conversation",
      placeholder: "How constant ringing affects daily function — sleep, focus, social.",
      whyItMatters: "Tinnitus is rated at 10% (DC 6260) regardless of severity, but the functional impact still matters for secondary claims (insomnia, anxiety).",
      chips: [
        { label: "Sleep disrupted", insert: "The ringing interferes with falling asleep most nights." },
        { label: "Concentration affected", insert: "I struggle to concentrate in quiet environments because the ringing is louder." },
        { label: "Conversation hard", insert: "In groups / restaurants, the ringing makes conversation harder to follow." },
        { label: "Worse when tired", insert: "The ringing is worse when I am tired or stressed." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — hearing aids, white noise, audiology",
      placeholder: "Audiology visits, hearing aids, masking devices.",
      whyItMatters: "Even tinnitus-only treatment (white noise, masking) corroborates the claim.",
      chips: [
        { label: "Audiology", insert: "I have been seen by VA audiology since [year]." },
        { label: "Hearing aids", insert: "I was prescribed hearing aids in [year]." },
        { label: "White noise", insert: "I sleep with white noise / a fan to mask the ringing." },
      ],
    },
  ],
};

const SLEEP_APNEA_PACKAGE = {
  goalTemplate: () =>
    "Explain when sleep problems began, what duty exposures may have contributed, how symptoms continued, " +
    "how the diagnosis was confirmed (sleep study), and how CPAP / sleep disruption affects daily function today.",
  prompts: [
    {
      id: "onset",
      section: "service-onset",
      label: "When did sleep problems start?",
      placeholder: "Approximate onset of snoring, daytime fatigue, witnessed apneas.",
      whyItMatters: "Sleep apnea claims often hinge on linking in-service snoring / fatigue / witnessed apneas to a later diagnosis.",
      chips: [
        { label: "Noticed during service", insert: "Sleep problems (snoring / fatigue / witnessed apneas) began during service." },
        { label: "Bunkmate witnessed", insert: "A bunkmate / spouse witnessed me stopping breathing and gasping in my sleep." },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service exposures + symptoms",
      placeholder: "Sleep deprivation, irregular shifts, weight gain, snoring observed.",
      whyItMatters: "Witnessed in-service snoring or apnea events from buddies are strong evidence.",
      chips: [
        { label: "Sleep deprivation", insert: "My duty involved chronic sleep deprivation and irregular shifts." },
        { label: "Weight gain in service", insert: "I gained weight during service which may have contributed." },
        { label: "Snoring noted", insert: "Buddies / bunkmates regularly commented on my snoring." },
      ],
    },
    {
      id: "diagnosis",
      section: "treatment",
      label: "Sleep study + diagnosis",
      placeholder: "When and where were you diagnosed? AHI? Prescribed CPAP?",
      whyItMatters: "Sleep apnea ratings (DC 6847) hinge on diagnosed severity and CPAP requirement.",
      chips: [
        { label: "Sleep study", insert: "I had a sleep study at [VA / civilian] in [month/year] that confirmed sleep apnea." },
        { label: "AHI", insert: "My AHI was [number]." },
        { label: "Prescribed CPAP", insert: "I was prescribed CPAP and use it nightly." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity from service to diagnosis",
      placeholder: "Symptoms between separation and formal diagnosis.",
      whyItMatters: "Continuity bridges the gap if you weren't formally diagnosed in service.",
      chips: [
        { label: "Symptoms continued post-service", insert: "Snoring / fatigue continued after separation." },
        { label: "Diagnosed post-service", insert: "I was diagnosed [X] years after separation." },
      ],
    },
    {
      id: "current-impact",
      section: "current-impact",
      label: "Current impact — fatigue, work, daytime function",
      placeholder: "Daytime sleepiness, missed work, driving safety, mood.",
      whyItMatters: "Daytime sleepiness + occupational impact strengthen any later TDIU / IU argument.",
      chips: [
        { label: "Daytime fatigue", insert: "I am chronically tired during the day despite using CPAP." },
        { label: "Fell asleep at work", insert: "I have fallen asleep at work / while driving due to fatigue." },
        { label: "Missed work", insert: "I have missed work due to fatigue." },
        { label: "Mood affected", insert: "Sleep deprivation has affected my mood / relationships." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — CPAP usage, compliance, other meds",
      placeholder: "CPAP nightly hours, mask issues, follow-up.",
      whyItMatters: "Documenting CPAP compliance keeps the rating intact (DC 6847 50% requires use of breathing assistance device).",
      chips: [
        { label: "Nightly CPAP", insert: "I use CPAP nightly, [X] hours per night per the machine data." },
        { label: "Mask issues", insert: "I have had to swap masks / pressures due to fit issues." },
        { label: "VA follow-up", insert: "I follow up with VA sleep clinic every [interval]." },
      ],
    },
  ],
};

const MIGRAINE_PACKAGE = {
  goalTemplate: () =>
    "Explain when migraines started, what in-service event (TBI, blast, head trauma, stress) caused them, " +
    "how often they occur now, and whether they are prostrating — meaning you cannot function and must lie down.",
  prompts: [
    {
      id: "onset",
      section: "service-onset",
      label: "Onset of migraines",
      placeholder: "Approximate date + circumstance — TBI, blast, post-injury, post-stress.",
      whyItMatters: "Migraines are often secondary to TBI, blast exposure, or another condition. Document the trigger.",
      chips: [
        { label: "After TBI / blast", insert: "Migraines began after a TBI / blast exposure in [date / location]." },
        { label: "During deployment", insert: "Migraines began during deployment to [location]." },
        { label: "After head trauma", insert: "Migraines began after a head trauma during service." },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service cause / trigger",
      placeholder: "Specific event(s) or duty exposures.",
      whyItMatters: "Service connection requires linking migraines to a duty exposure or to a service-connected condition (TBI, MH).",
      chips: [
        { label: "Blast / IED", insert: "I was near a blast / IED that may have caused TBI." },
        { label: "Combat stress", insert: "Migraines were tied to combat stress / high-tempo duty." },
        { label: "Sleep deprivation", insert: "Migraines were worse during periods of sleep deprivation." },
      ],
    },
    {
      id: "frequency",
      section: "current-impact",
      label: "Migraine frequency + prostrating attacks",
      placeholder: "How often per month. How many require you to lie down in a dark quiet room.",
      whyItMatters: "Migraine ratings (DC 8100) hinge on PROSTRATING attacks per month — 1 per 2 months = 10%, 1/month = 30%, very frequent prostrating + severe economic impact = 50%.",
      chips: [
        { label: "1/month", insert: "I have about 1 migraine per month." },
        { label: "2–3/month", insert: "I have 2–3 migraines per month." },
        { label: "Weekly", insert: "I have a migraine roughly once a week." },
        { label: "Prostrating", insert: "When a migraine hits, I cannot function — I have to lie down in a dark, quiet room." },
        { label: "Lasts hours", insert: "A migraine typically lasts [X] hours before subsiding." },
      ],
    },
    {
      id: "symptoms",
      section: "symptoms-service",
      label: "Symptoms of an attack",
      placeholder: "Pain location, aura, photophobia, nausea, vomiting.",
      whyItMatters: "Documenting classic migraine features (aura, photophobia, nausea) helps tie the diagnosis.",
      chips: [
        { label: "Aura", insert: "Migraines are preceded by aura (visual disturbances / numbness)." },
        { label: "Photophobia", insert: "Light makes the headache worse." },
        { label: "Nausea / vomiting", insert: "I get nauseous and sometimes vomit during attacks." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity since service",
      placeholder: "Have migraines continued? Worsened? Any gaps in care?",
      whyItMatters: "Continuity proves the migraines didn't resolve.",
      chips: [
        { label: "Continuous since service", insert: "Migraines have continued since separation." },
        { label: "Worsened", insert: "Frequency / severity has worsened." },
      ],
    },
    {
      id: "work-impact",
      section: "current-impact",
      label: "Work / economic impact",
      placeholder: "Missed work / school / family obligations because of migraines.",
      whyItMatters: "Migraine 50% rating requires severe ECONOMIC inadaptability — quantify missed work days.",
      chips: [
        { label: "Missed work days", insert: "I have missed [X] days of work in the last 12 months due to migraines." },
        { label: "Left work early", insert: "I have left work early multiple times due to a migraine." },
        { label: "Modified duties", insert: "I am on modified duties because of migraines." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — meds, abortive, preventive",
      placeholder: "Daily preventives, abortive meds, neurology follow-up.",
      whyItMatters: "Documenting failed / current meds shows the condition is real and treatment-resistant where applicable.",
      chips: [
        { label: "Preventive med", insert: "I take [med] daily as a preventive." },
        { label: "Abortive med", insert: "I take [med] when a migraine starts." },
        { label: "Neurology", insert: "I see VA / civilian neurology for migraines." },
        { label: "Botox", insert: "I receive Botox injections for chronic migraine." },
      ],
    },
  ],
};

const DEFAULT_PACKAGE = {
  goalTemplate: ({ conditionName }) =>
    `Explain when your ${conditionName || "condition"} started, what in-service event or exposure caused it, ` +
    `how symptoms continued since service, and how it affects your daily function and work today.`,
  prompts: [
    {
      id: "onset",
      section: "service-onset",
      label: "Onset",
      placeholder: "Approximate date the condition started and what was happening at the time.",
      whyItMatters: "A date anchor lets reviewers locate this in your record.",
      chips: [
        { label: "During service", insert: "Symptoms began while I was on active duty." },
        { label: "After service", insert: "Symptoms appeared shortly after separation." },
      ],
    },
    {
      id: "service-event",
      section: "service-onset",
      label: "In-service event / exposure",
      placeholder: "Specific incident, duty, or exposure that started or aggravated this.",
      whyItMatters: "Service connection is the hinge of the claim. Be concrete.",
      chips: [
        { label: "Specific event", insert: "On [date] during [duty / location], [what happened]." },
        { label: "Gradual exposure", insert: "Years of [duty activity] gradually caused / worsened this." },
      ],
    },
    {
      id: "symptoms-then",
      section: "symptoms-service",
      label: "Symptoms during service",
      placeholder: "What symptoms you had, how often, and whether you reported them.",
      whyItMatters: "In-service symptom evidence (sick-call, profiles, buddy memories) builds the timeline.",
      chips: [
        { label: "Sick call", insert: "I went to sick call for this." },
        { label: "Buddies noticed", insert: "Fellow service members noticed and have offered to provide statements." },
      ],
    },
    {
      id: "continuity",
      section: "continuity",
      label: "Continuity since separation",
      placeholder: "Symptoms after leaving service. Any care gaps + why.",
      whyItMatters: "Continuity proves the in-service problem never resolved.",
      chips: [
        { label: "Symptoms continued", insert: "Symptoms have continued since separation." },
        { label: "Worsened", insert: "Symptoms have worsened over time." },
      ],
    },
    {
      id: "current-impact",
      section: "current-impact",
      label: "Current functional impact",
      placeholder: "How this affects work, sleep, relationships, daily tasks.",
      whyItMatters: "Functional impact drives the rating. Translate symptoms into real activities.",
      chips: [
        { label: "Work impact", insert: "This affects my ability to [work activity]." },
        { label: "Sleep impact", insert: "This disrupts my sleep." },
        { label: "Daily tasks", insert: "Daily tasks such as [examples] are limited." },
      ],
    },
    {
      id: "treatment",
      section: "treatment",
      label: "Treatment — meds, providers, devices, therapy",
      placeholder: "Current treatment, providers, meds, devices.",
      whyItMatters: "Treatment evidence corroborates symptom severity.",
      chips: [
        { label: "Medication", insert: "I take [med + dose + frequency]." },
        { label: "Provider", insert: "I follow up at [VA / clinic] with [provider]." },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// 3. Package registry + accessor
// ---------------------------------------------------------------------------

const PACKAGES = {
  knee:         KNEE_PACKAGE,
  back:         BACK_PACKAGE,
  shoulder:     makeMskPackage("shoulder", "Shoulder ratings (DC 5201) hinge on degrees of motion + whether the arm reaches above shoulder level."),
  hip:          makeMskPackage("hip",      "Hip ratings (DC 5251–5253) hinge on degrees of flexion / abduction / external rotation."),
  ankle:        makeMskPackage("ankle",    "Ankle ratings (DC 5271) hinge on degrees of motion + functional limit."),
  neck:         makeMskPackage("neck",     "Neck (cervical) ratings (38 CFR §4.71a) hinge on degrees of forward flexion + functional limit + radiculopathy."),
  "wrist-hand": makeMskPackage("wrist",    "Wrist/hand ratings hinge on degrees of motion and grip strength."),
  foot:         makeMskPackage("foot",     "Foot conditions (DC 5276+) hinge on weight-bearing function + tenderness."),
  tinnitus:     TINNITUS_PACKAGE,
  "hearing-loss": makeMskPackage("hearing", "Hearing loss ratings (DC 6100) hinge on puretone + speech-discrimination audiogram results."),
  "sleep-apnea": SLEEP_APNEA_PACKAGE,
  migraine:     MIGRAINE_PACKAGE,
  ibs:          makeMskPackage("digestive", "IBS / GERD ratings hinge on frequency + severity of episodes."),
  skin:         makeMskPackage("skin", "Skin ratings hinge on percentage of body covered + treatment required (immunosuppressives bump the rating)."),
  default:      DEFAULT_PACKAGE,
};

/**
 * Resolve the condition package + goal copy + final prompt list for a
 * given condition name. Used by the editor.
 */
export function getConditionPackage(conditionName) {
  const cat = categorize(conditionName);
  const side = detectSide(conditionName);
  const pkg = PACKAGES[cat] || PACKAGES.default;
  const goal = pkg.goalTemplate ? pkg.goalTemplate({ side, conditionName }) : "";
  return {
    category: cat,
    side,
    goal,
    prompts: pkg.prompts,
  };
}

/**
 * Smart chip insert: take the current textarea value and a chip insert string,
 * and return the new value. Logic:
 *   - If the textarea is empty, just use the insert.
 *   - If the insert begins with " — " or " —" (an em-dash continuation), append.
 *   - Otherwise, append on a new sentence.
 */
export function applyChipInsert(currentValue, chipInsert) {
  const cur = String(currentValue || "");
  const ins = String(chipInsert || "");
  if (!cur.trim()) return ins;
  if (ins.startsWith(" —") || ins.startsWith(" –")) return cur.trim() + ins;
  const sep = cur.trim().endsWith(".") || cur.trim().endsWith("?") || cur.trim().endsWith("!") ? " " : ". ";
  return cur.trim() + sep + ins;
}

// ---------------------------------------------------------------------------
// 4. Gap classification — Required vs Recommended
// ---------------------------------------------------------------------------

/**
 * Split the 8 audit dimensions into two buckets the editor renders in the
 * Gaps panel. "required" gaps block a Strong statement; "recommended" gaps
 * are strengtheners.
 */
const REQUIRED_DIMS = new Set([
  "timeline", "in-service-event", "current-impact", "continuity",
]);
const RECOMMENDED_DIMS = new Set([
  "current-diagnosis", "treatment", "evidence-linked", "plain-language-credibility",
]);

export function classifyGaps(dimensions) {
  if (!Array.isArray(dimensions)) return { required: [], recommended: [] };
  const open = dimensions.filter((d) => d.status !== "strong");
  return {
    required: open.filter((d) => REQUIRED_DIMS.has(d.id)),
    recommended: open.filter((d) => RECOMMENDED_DIMS.has(d.id)),
  };
}

/**
 * For a given dimension id, return the FIRST prompt id (from the provided
 * prompt list) whose `section` aligns with that dimension. Used so "Fix now"
 * knows which prompt to scroll to. Falls back to the first prompt if no
 * mapping is found.
 */
const DIM_TO_SECTION = {
  "timeline":         ["service-onset", "continuity"],
  "in-service-event": ["service-onset"],
  "current-impact":   ["current-impact"],
  "continuity":       ["continuity"],
  "current-diagnosis":["current-impact", "treatment"],
  "treatment":        ["treatment"],
  "evidence-linked":  ["service-onset", "treatment", "current-impact"],
  "plain-language-credibility": ["current-impact"],
};

export function dimensionToPromptId(dimId, prompts) {
  const sections = DIM_TO_SECTION[dimId] || [];
  for (const sec of sections) {
    const p = prompts.find((pp) => pp.section === sec);
    if (p) return p.id;
  }
  return prompts[0]?.id || null;
}
